import React, { useCallback, useState } from "react";
import { TreeItemIndex } from "react-complex-tree";
import { useSelector } from "react-redux";
import { InfoOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";

import { getCurrentProjectData } from "fond/project";
import { FeatureListTypes, FeaturesTable } from "fond/project/features";
import { Store } from "fond/types";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { BlockSpinner, NonIdealState } from "fond/widgets";

import { selectAllVersionLayerConfigsInOrder, selectVersionConfig, useGetVersionStatusQuery } from "../../api";

import LayerSelection from "./LayerSelection";

import { Container, LeftPanel, RightPanel } from "./FeaturesList.styles";

interface FeaturesListProps {
  /*
   * Feature lists can be composed of all 'design' layers or all 'errors' layers.
   */
  type: FeatureListTypes;
}

const FeaturesList: React.FC<FeaturesListProps> = ({ type }: FeaturesListProps) => {
  const data = useSelector((state: Store) => getCurrentProjectData(state.project));
  const versionId = useSelector((state: Store): string => state.project.versionId);
  const { data: status } = useGetVersionStatusQuery(versionId, { skip: !versionId });
  const hasErrors = (status?.Error?.length ?? 0) > 0;
  const [selection, setSelection] = useState<{ layer: LayerConfig; sublayer?: SublayerConfig }>();
  const configuration = useSelector((state: Store) => selectVersionConfig(state, versionId));
  const layerConfigs = useSelector((state: Store) => selectAllVersionLayerConfigsInOrder(state, versionId));

  const handleOnChange = useCallback(
    (items: TreeItemIndex[]) => {
      const id = items.at(0);
      if (id) {
        const entity = configuration.Data.entities[id] as LayerConfig | SublayerConfig;
        if (entity.Type === "LAYER") {
          setSelection({ layer: entity });
        } else if (entity.Type === "SUBLAYER") {
          const layer = configuration.Data.entities[entity.ParentID];
          if (layer && layer.Type === "LAYER") {
            setSelection({ layer, sublayer: entity });
          }
        }
      }
    },
    [configuration.Data.entities]
  );

  // If no layers data has not yet loaded
  if (!layerConfigs || !data) {
    return <BlockSpinner />;
  }

  if (type === FeatureListTypes.ERRORS && !hasErrors) {
    return <NonIdealState icon={<InfoOutlined />} size="small" description="There are currently no design errors relating to this project" />;
  }

  if (type === FeatureListTypes.DESIGN && layerConfigs.length === 0) {
    return <NonIdealState icon={<InfoOutlined />} size="small" description="There is currently no design information for this version" />;
  }

  return (
    <Container data-testid="layer-attributes-panel">
      <LeftPanel>
        <Box className="customScrollbars">
          <LayerSelection onChange={handleOnChange} type={type} />
        </Box>
      </LeftPanel>
      <RightPanel>
        {selection?.layer != null ? (
          <FeaturesTable layerConfig={selection.layer} sublayerConfig={selection.sublayer} showIds={type === FeatureListTypes.ERRORS} />
        ) : (
          <NonIdealState icon={<InfoOutlined />} size="small" description="Select a layer to view its features" />
        )}
      </RightPanel>
    </Container>
  );
};

export default FeaturesList;
